import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'src/hooks/redux';
import { selectDataSuggested, selectProfileCompletenessNotice } from 'src/redux/shopping_cart/shopping_cart.selectors';
import {
    formatExpirationMonthKey,
    summarizeActiveProductsByExpiration,
} from 'src/helpers/activeProductsByExpiration';
import Icon from 'src/components/common/Icon';
import Modal from 'src/components/common/modal/Modal';
import UserProfileEditModal from '../calculo-sugeridos/suggested-form/UserProfileEditModal';
import { ProfileNoticeAlert } from '../calculo-sugeridos/styles';
import ContactIcon, { ContactIconName } from './UserInfoIcons';
import {
    AccordionBar,
    AccordionContent,
    ChipIcon,
    ChipLabel,
    ChipMain,
    Column,
    ContactCard,
    ContactCardHeader,
    ContactColumns,
    EditButton,
    EmptyExpiration,
    ExpirationTable,
    Footer,
    InfoRow,
    InfoRowLabel,
    InfoRowValue,
    MetricHint,
    MetricLabel,
    MetricRow,
    MetricsGrid,
    MetricValue,
    OtpBadge,
    OuterBody,
    OuterCard,
    OuterHeader,
    PcomProductsRow,
    ProductCell,
    ProductChip,
    SectionPill,
    StatusBadge,
    StatusPill,
    TableWrapper,
} from './UserInfoSummary.styles';

export const EMPTY_EXPIRING_PRODUCTS_COPY =
    'No hay productos por vencer en los próximos 90 días.';

export const FOOTER_COPY =
    'La información mostrada corresponde al estado actual del anunciante en Propiedades.com.';

/**
 * Columnas de productos del cronograma de expiración.
 * - `key`: clave para buscar el conteo del mes (proviene del helper).
 * - `header`: texto visible de la columna.
 * - `asStatus`: muestra "Activo" en vez del conteo cuando hay dato.
 */
const EXPIRATION_PRODUCT_COLUMNS = [
    { key: 'Primes', header: 'Primes', asStatus: false, center: true },
    { key: 'Destacados', header: 'Destacados', asStatus: false, center: true },
    { key: 'Elite', header: 'Elite', asStatus: true, center: true },
    // "Oportunidades ilimitadas": se alimenta de los ítems name_product "OI" de
    // active_products_by_expiration_month que vencen en los próximos 90 días.
    { key: 'Oportunidades', header: 'Oportunidades ilimitadas', asStatus: true, center: true },
] as const;

/** `name_product` de "Oportunidades ilimitadas" en active_products_by_expiration_month. */
const OI_NAME_PRODUCT = 'OI';

/** Ventana (en días) para considerar un producto OI como "por vencer". */
const OI_EXPIRATION_WINDOW_DAYS = 90;

/** Texto de la celda de producto: "[ - ]" vacío, "Activo" si es de estado, o "[ N ]". */
const expirationCellText = (count: number, asStatus: boolean): string => {
    if (count === 0) return '[ - ]';
    if (asStatus) return 'Activo';
    return `[ ${count} ]`;
};

/** True si `endDate` (YYYY-MM-DD...) vence entre hoy y los próximos `days` días. */
const expiresWithinDays = (endDate: string | null | undefined, days: number): boolean => {
    if (!endDate) return false;
    const end = new Date(endDate);
    if (Number.isNaN(end.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limit = new Date(today);
    limit.setDate(limit.getDate() + days);
    return end >= today && end <= limit;
};

/** Clave `YYYY-MM` del mes actual (para marcar el estado "Próximo a vencer"). */
const currentMonthKey = (): string => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Fila etiqueta → valor de la sección "Contacto básico". */
const ContactInfoRow = ({
    icon,
    label,
    value,
    verified,
    active,
    bold,
    badgeLeading,
}: {
    icon: ContactIconName;
    label: string;
    value: React.ReactNode;
    verified?: boolean;
    /** Estado informativo (solo lectura): muestra badge "Activo"/"Inactivo". */
    active?: boolean;
    bold?: boolean;
    /**
     * Si es `true`, el badge de validación (OTP / Sin validar) se muestra ANTES
     * del valor. Se usa en los correos para que el texto quede alineado a la
     * derecha con el resto de los datos (el badge no lo desplaza).
     */
    badgeLeading?: boolean;
}) => {
    const otpBadge = verified !== undefined && (
        <OtpBadge $verified={verified}>
            <ContactIcon name={verified ? 'check' : 'close'} size={13} />
            {verified ? 'OTP' : 'Sin validar'}
        </OtpBadge>
    );

    return (
        <InfoRow>
            <InfoRowLabel>
                <ContactIcon className="row-icon" name={icon} size={16} />
                {label}
            </InfoRowLabel>
            <InfoRowValue $bold={bold}>
                {badgeLeading && otpBadge}
                {value}
                {!badgeLeading && otpBadge}
                {active !== undefined && (
                    <StatusPill $on={active}>
                        {active && <ContactIcon name="check" size={13} />}
                        {active ? 'Activo' : 'Inactivo'}
                    </StatusPill>
                )}
            </InfoRowValue>
        </InfoRow>
    );
};

/**
 * Información del usuario (tab "Información del usuario").
 * Se nutre de `suggested_calculator` (estado `shopping_cart.suggested`).
 */
const UserInfoSummary = () => {
    const [showModal, setShowModal] = useState(false);
    const [outerOpen, setOuterOpen] = useState(true);
    const [contactOpen, setContactOpen] = useState(true);
    const [pcomOpen, setPcomOpen] = useState(false);
    const [expirationOpen, setExpirationOpen] = useState(false);

    const { suggested } = useAppSelector(selectDataSuggested);
    const profileNotice = useAppSelector(selectProfileCompletenessNotice);

    const expirationSummaries = useMemo(
        () => summarizeActiveProductsByExpiration(suggested.active_products_by_expiration_month),
        [suggested.active_products_by_expiration_month]
    );

    const profile = suggested.user_profile;
    // suggested_calculator distribuye los campos en distintos niveles:
    // - user_profile: broker_type, freemium_state, has_active_elite, etc.
    // - portfolio_metrics: total_properties, total_active_properties, etc.
    // - a veces en la raíz (ver redux/shopping_cart/api.tsx).
    // Fusionamos las tres fuentes para que los valores siempre se muestren.
    const source = useMemo(() => {
        const root = (suggested as unknown) as Record<string, unknown>;
        const userProfile = ((profile ?? {}) as unknown) as Record<string, unknown>;
        const portfolioMetrics =
            (userProfile.portfolio_metrics as Record<string, unknown> | undefined) ??
            (root.portfolio_metrics as Record<string, unknown> | undefined) ??
            {};
        return {
            ...root,
            ...userProfile,
            ...portfolioMetrics,
        };
    }, [suggested, profile]);

    const getNumber = (key: string): number => {
        const value = source[key];
        return typeof value === 'number' ? value : 0;
    };
    const getText = (key: string): string => {
        const value = source[key];
        return typeof value === 'string' && value !== '' ? value : '-';
    };
    // Devuelve solo la fecha (YYYY-MM-DD), sin la hora.
    const getDate = (key: string): string => {
        const value = source[key];
        if (typeof value !== 'string' || value === '') return '-';
        return value.split('T')[0];
    };

    // "Producto Elite": Sí/No según has_active_elite (acepta boolean, 1/0 o "true").
    const eliteRaw = source.has_active_elite;
    const hasActiveElite = eliteRaw === true || eliteRaw === 1 || eliteRaw === 'true';

    // "Oportunidades ilimitadas": Sí/No según has_active_oi (acepta boolean, 1/0 o "true").
    const oiRaw = source.has_active_oi;
    const hasActiveOi = oiRaw === true || oiRaw === 1 || oiRaw === 'true';

    // "Prime": Sí/No según has_active_prime (acepta boolean, 1/0 o "true").
    // Refleja que el usuario tiene al menos un Prime activo, sin depender de la
    // ventana de vencimiento de active_products_by_expiration_month.
    const primeRaw = source.has_active_prime;
    const hasActivePrime = primeRaw === true || primeRaw === 1 || primeRaw === 'true';

    const userId = suggested.users.user_id;
    const name = suggested.user_profile?.name || '';
    const lastname = suggested.user_profile?.lastname || '';
    const email = suggested.users.email || '';
    const phoneContact = suggested.user_profile?.phone_contact || '';
    const phoneWhatsapp = suggested.user_profile?.phone_whatsapp || '';
    const brokerType = getText('broker_type');
    const sellerEmail = suggested.pipedrive_seller?.email || '';
    const feedName = suggested.user_profile?.feed_name || '';
    const isCrm =
        suggested.user_profile?.from_xml === 1 && suggested.user_profile?.feed_name !== '';

    // Canales de contacto verificados por OTP (otp_channel_verifications).
    const otp = suggested.user_profile?.otp_channel_verifications;

    const emailContact = otp?.email_contact;
    const emailContactValue = emailContact?.value || '';
    const emailContactVerified = !!(emailContact?.verified && emailContact.value);

    const accountEmailVerified = !!(otp?.personal_email?.verified && otp.personal_email.value);

    // Teléfono de contacto: valor verificado por OTP o, en su defecto, phone_contact.
    const smsOtp = otp?.sms;
    const telContactVerified = !!(smsOtp?.verified && smsOtp.value);
    const telContactValue = telContactVerified ? smsOtp!.value : phoneContact;

    // WhatsApp: valor verificado por OTP o, en su defecto, phone_whatsapp.
    const whatsappOtp = otp?.whatsapp;
    const whatsappVerified = !!(whatsappOtp?.verified && whatsappOtp.value);
    const whatsappValue = whatsappVerified ? whatsappOtp!.value : phoneWhatsapp;

    // Meses con un producto OI ("Oportunidades ilimitadas") que vence en los próximos 90 días.
    const oiExpiringByMonth = useMemo(() => {
        const map: Record<string, boolean> = {};
        Object.entries(suggested.active_products_by_expiration_month ?? {}).forEach(
            ([monthKey, items]) => {
                const hasOiSoon = (items ?? []).some(
                    (item) =>
                        (item?.name_product ?? '').trim() === OI_NAME_PRODUCT &&
                        expiresWithinDays(item?.end_date, OI_EXPIRATION_WINDOW_DAYS)
                );
                if (hasOiSoon) map[monthKey] = true;
            }
        );
        return map;
    }, [suggested.active_products_by_expiration_month]);

    // Cronograma de expiración: cuenta por mes y por tipo de producto.
    const expirationRows = useMemo(() => {
        const nowKey = currentMonthKey();
        const byMonth = new Map<string, { monthLabel: string; counts: Record<string, number> }>();

        // Productos contados por el helper (Primes/Destacados/Elite/Premium).
        expirationSummaries.forEach(({ monthKey, monthLabel, rows }) => {
            const counts: Record<string, number> = {};
            rows.forEach(({ label, count }) => {
                counts[label] = count;
            });
            byMonth.set(monthKey, { monthLabel, counts });
        });

        // "Oportunidades ilimitadas": marca el mes como activo si vence en 90 días.
        Object.keys(oiExpiringByMonth).forEach((monthKey) => {
            const existing = byMonth.get(monthKey);
            if (existing) {
                existing.counts.Oportunidades = 1;
            } else {
                byMonth.set(monthKey, {
                    monthLabel: formatExpirationMonthKey(monthKey),
                    counts: { Oportunidades: 1 },
                });
            }
        });

        return Array.from(byMonth.entries())
            .sort(([a], [b]) => a.localeCompare(b, 'es-MX'))
            .map(([monthKey, { monthLabel, counts }]) => ({
                monthKey,
                monthLabel,
                counts,
                isExpiringSoon: monthKey <= nowKey,
            }))
            // El helper aún cuenta Premium (lo usa el formulario antiguo), pero esa
            // columna ya no se muestra aquí: omite los meses sin productos visibles.
            .filter(({ counts }) =>
                EXPIRATION_PRODUCT_COLUMNS.some(({ key }) => (counts[key] ?? 0) > 0)
            );
    }, [expirationSummaries, oiExpiringByMonth]);

    // Productos que el usuario tiene activos (según active_products_by_expiration_month).
    const activeProductNames = useMemo(() => {
        const set = new Set<string>();
        Object.values(suggested.active_products_by_expiration_month ?? {}).forEach((items) => {
            (items ?? []).forEach((item) => {
                if (item?.name_product) set.add(item.name_product.trim());
            });
        });
        return set;
    }, [suggested.active_products_by_expiration_month]);

    // Productos PCOM (solo lectura, reflejan el estado del usuario).
    const pcomProducts: { key: string; label: string; icon: ContactIconName; on: boolean }[] = [
        { key: 'oportunidades', label: 'Oportunidades ilimitadas', icon: 'infinity', on: hasActiveOi },
        {
            key: 'destacados',
            label: 'Destacados',
            icon: 'star',
            on: activeProductNames.has('Slot') || getNumber('highlighted_properties') > 0,
        },
        { key: 'prime', label: 'Prime', icon: 'crown', on: hasActivePrime || activeProductNames.has('Slot destacado') },
        { key: 'elite', label: 'Elite', icon: 'gem', on: hasActiveElite },
    ];

    // Fecha en formato DD-MM-YYYY.
    const formatDateDMY = (key: string): string => {
        const value = getDate(key);
        if (value === '-') return '-';
        const [y, m, d] = value.split('-');
        return y && m && d ? `${d}-${m}-${y}` : value;
    };

    const scorePct = Math.round(getNumber('score_percentage_high'));

    return (
        <>
            <OuterCard>
                <OuterHeader type="button" onClick={() => setOuterOpen((v) => !v)}>
                    <span className="header-title">
                        <Icon icon="pcom-icon-user" size={18} />
                        Información del anunciante{email ? ` · ${email}` : ''}
                    </span>
                    <Icon
                        className="header-chevron"
                        icon={outerOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                        size={20}
                    />
                </OuterHeader>

                {outerOpen && (
                    <OuterBody>
                        {/* 1. Contacto básico */}
                        <ContactCard>
                            <ContactCardHeader>
                                <SectionPill
                                    type="button"
                                    onClick={() => setContactOpen((v) => !v)}
                                >
                                    Contacto básico
                                    <Icon
                                        className="pill-chevron"
                                        icon={contactOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                                        size={16}
                                    />
                                </SectionPill>
                                <EditButton
                                    type="button"
                                    data-id="button-open-modal"
                                    onClick={() => setShowModal(true)}
                                >
                                    <ContactIcon name="user" size={16} />
                                    Editar perfil
                                </EditButton>
                            </ContactCardHeader>

                            {profileNotice && (
                                <ProfileNoticeAlert>
                                    <Icon icon="pcom-icon-warning-filled" size={16} />
                                    {profileNotice}
                                </ProfileNoticeAlert>
                            )}

                            {contactOpen && (
                                <ContactColumns>
                                    <Column>
                                        <ContactInfoRow
                                            icon="id"
                                            label="ID"
                                            value={`#${userId}`}
                                        />
                                        {name && (
                                            <ContactInfoRow
                                                icon="user"
                                                label="Nombre"
                                                value={name}
                                            />
                                        )}
                                        {lastname && (
                                            <ContactInfoRow
                                                icon="user-square"
                                                label="Apellido"
                                                value={lastname}
                                            />
                                        )}
                                        {emailContactValue && (
                                            <ContactInfoRow
                                                icon="mail"
                                                label="Email contacto"
                                                value={emailContactValue}
                                                verified={emailContactVerified}
                                                badgeLeading
                                            />
                                        )}
                                        {email && (
                                            <ContactInfoRow
                                                icon="mail"
                                                label="Email de la cuenta"
                                                value={email}
                                                verified={accountEmailVerified}
                                                badgeLeading
                                            />
                                        )}
                                    </Column>
                                    <Column>
                                        {telContactValue && (
                                            <ContactInfoRow
                                                icon="phone"
                                                label="Tel contacto"
                                                value={telContactValue}
                                                verified={telContactVerified}
                                            />
                                        )}
                                        {whatsappValue && (
                                            <ContactInfoRow
                                                icon="chat"
                                                label="WhatsApp"
                                                value={whatsappValue}
                                                verified={whatsappVerified}
                                            />
                                        )}
                                        {feedName && (
                                            <ContactInfoRow
                                                icon="rss"
                                                label="Feed"
                                                value={feedName}
                                                active={isCrm}
                                                bold
                                            />
                                        )}
                                        {brokerType !== '-' && (
                                            <ContactInfoRow
                                                icon="building"
                                                label="Tipo de broker"
                                                value={brokerType}
                                            />
                                        )}
                                        {sellerEmail && (
                                            <ContactInfoRow
                                                icon="briefcase"
                                                label="Gestor comercial"
                                                value={sellerEmail}
                                            />
                                        )}
                                    </Column>
                                </ContactColumns>
                            )}
                        </ContactCard>

                        {/* 2. Información PCOM (diseño provisional, pendiente de rediseño) */}
                        <div>
                            <AccordionBar
                                type="button"
                                $open={pcomOpen}
                                onClick={() => setPcomOpen((v) => !v)}
                            >
                                Información PCOM
                                <Icon
                                    className="bar-chevron"
                                    icon={pcomOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                                    size={20}
                                />
                            </AccordionBar>
                            {pcomOpen && (
                                <AccordionContent>
                                    <PcomProductsRow>
                                        {pcomProducts.map(({ key, label, icon, on }) => (
                                            <ProductChip key={key} $on={on}>
                                                <ChipMain>
                                                    <ChipIcon $on={on}>
                                                        <ContactIcon name={icon} size={16} />
                                                    </ChipIcon>
                                                    <ChipLabel>{label}</ChipLabel>
                                                </ChipMain>
                                                <StatusPill
                                                    $on={on}
                                                    data-id={`pcom-status-${key}`}
                                                    aria-label={`${label}: ${on ? 'Activo' : 'Inactivo'}`}
                                                >
                                                    {on && <ContactIcon name="check" size={13} />}
                                                    {on ? 'Activo' : 'Inactivo'}
                                                </StatusPill>
                                            </ProductChip>
                                        ))}
                                    </PcomProductsRow>

                                    <MetricsGrid>
                                        <MetricRow>
                                            <MetricLabel>Total propiedades</MetricLabel>
                                            <MetricValue>{getNumber('total_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Total propiedades activas</MetricLabel>
                                            <MetricValue>{getNumber('total_active_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Activas residenciales</MetricLabel>
                                            <MetricValue>{getNumber('active_residential_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Slots vacíos sin asignación</MetricLabel>
                                            <MetricValue>{getNumber('unassigned_active_slots')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Total propiedades destacadas</MetricLabel>
                                            <MetricValue>{getNumber('highlighted_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Fuera de mercado</MetricLabel>
                                            <MetricValue>{getNumber('out_of_market_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow $highlight>
                                            <MetricLabel>Score promedio</MetricLabel>
                                            <MetricValue $highlight>
                                                {scorePct}%
                                                <MetricHint>Meta ≥ 80</MetricHint>
                                            </MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Promedio de inventario activo</MetricLabel>
                                            <MetricValue>{Math.round(getNumber('avg_publication_score'))}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Zona principal de publicación</MetricLabel>
                                            <MetricValue>{getText('main_publication_zone')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Fecha última publicación</MetricLabel>
                                            <MetricValue>{formatDateDMY('last_publication_date')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Posibles Elite</MetricLabel>
                                            <MetricValue>{getNumber('elite_candidate_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Leads ofuscados</MetricLabel>
                                            <MetricValue>{getNumber('blocked_leads')}</MetricValue>
                                        </MetricRow>
                                    </MetricsGrid>
                                </AccordionContent>
                            )}
                        </div>

                        {/* 3. Productos a expirar (tabla sin cambios) */}
                        <div>
                            <AccordionBar
                                type="button"
                                $open={expirationOpen}
                                onClick={() => setExpirationOpen((v) => !v)}
                            >
                                Productos a expirar
                                <Icon
                                    className="bar-chevron"
                                    icon={expirationOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                                    size={20}
                                />
                            </AccordionBar>
                            {expirationOpen && (
                                <AccordionContent>
                                    {expirationRows.length === 0 ? (
                                        <EmptyExpiration>{EMPTY_EXPIRING_PRODUCTS_COPY}</EmptyExpiration>
                                    ) : (
                                        <TableWrapper>
                                            <ExpirationTable>
                                                <thead>
                                                    <tr>
                                                        <th>Mes de expiración</th>
                                                        {EXPIRATION_PRODUCT_COLUMNS.map(({ key, header, center }) => (
                                                            <th key={key} className={center ? 'center' : undefined}>
                                                                {header}
                                                            </th>
                                                        ))}
                                                        <th>Estado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {expirationRows.map(({ monthKey, monthLabel, counts, isExpiringSoon }) => (
                                                        <tr key={monthKey}>
                                                            <td className="month">{monthLabel}</td>
                                                            {EXPIRATION_PRODUCT_COLUMNS.map(({ key, asStatus, center }) => {
                                                                const count = counts[key] ?? 0;
                                                                const isEmpty = count === 0;
                                                                return (
                                                                    <td key={key} className={center ? 'center' : undefined}>
                                                                        <ProductCell
                                                                            data-empty={isEmpty}
                                                                            data-active={!isEmpty && asStatus}
                                                                        >
                                                                            {expirationCellText(count, asStatus)}
                                                                        </ProductCell>
                                                                    </td>
                                                                );
                                                            })}
                                                            <td>
                                                                <StatusBadge $danger={isExpiringSoon}>
                                                                    {isExpiringSoon ? 'Próximo a vencer' : 'Estable'}
                                                                </StatusBadge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </ExpirationTable>
                                        </TableWrapper>
                                    )}
                                </AccordionContent>
                            )}
                        </div>

                        <Footer>
                            <Icon className="footer-icon" icon="pcom-icon-check-circle" size={16} />
                            {FOOTER_COPY}
                        </Footer>
                    </OuterBody>
                )}
            </OuterCard>

            {showModal && email !== '' && (
                <Modal onClose={() => setShowModal(false)}>
                    <UserProfileEditModal onClose={() => setShowModal(false)} />
                </Modal>
            )}
        </>
    );
};

export default UserInfoSummary;
